import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Modal } from "react-bootstrap";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import {
  reasonLabel,
  typeLabel,
  resolveScenario,
  ACTION_SEVERITY,
  isAggressiveHide,
  weakensSevereRule,
  WILDCARD_REASON_LABEL,
  WILDCARD_TYPE_LABEL,
} from "./rulePolicy";

function actionLabel(value, actionOptions) {
  return actionOptions.find((o) => o.value === value)?.label || value;
}

// Create/edit a single moderation rule. Clones OutletConfigModal's write mechanics
// (authenticatedHeadersHash + CSRF + JSON). reason/type are the rule's key: editable
// on create, fixed on edit (changing the key is delete + create). An interactive
// effect simulator lets the admin dial report counts and see the resolved outcome
// (and whether THIS rule takes effect) — because which rule wins depends on the
// actual counts, not a fixed order. Confirms the two dangerous edit directions
// before saving (a warning, not a hard block).
function ModerationRuleModal({
  rule = null,
  allRules,
  actionOptions,
  reasonKeys,
  reasonLabels,
  typeKeys,
  typeLabels,
  severeReasons,
  onSaved,
  onClose,
}) {
  const isEdit = Boolean(rule?.id);
  const [reason, setReason] = useState(rule?.reason || "");
  const [reportableType, setReportableType] = useState(rule?.reportable_type || "");
  const [action, setAction] = useState(rule?.action || actionOptions[0]?.value || "queue_for_review");
  const [threshold, setThreshold] = useState(rule?.threshold || 1);
  const [enabled, setEnabled] = useState(rule?.enabled ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Effect-simulator inputs. For a reason-specific rule the admin dials two axes
  // (reports of this reason, and of other reasons); an any-reason rule has one axis
  // (total reports). Seeded so the rule's own threshold is just met.
  const [thisReasonReports, setThisReasonReports] = useState(Number(rule?.threshold) || 1);
  const [otherReports, setOtherReports] = useState(0);

  const candidate = useMemo(
    () => ({
      id: rule?.id,
      reason: reason || null,
      reportable_type: reportableType || null,
      action,
      threshold: Number(threshold),
      enabled,
    }),
    [rule?.id, reason, reportableType, action, threshold, enabled],
  );

  // The policy as it WOULD be with this edit applied, for the effect simulator.
  const previewRules = useMemo(() => {
    const others = allRules.filter((r) => r.id !== rule?.id);
    return [...others, candidate];
  }, [allRules, candidate, rule?.id]);

  const isAnyReasonRule = candidate.reason === null;
  // An any-reason rule has no "this reason" axis — its single input is the total.
  const scenarioThisReason = isAnyReasonRule ? 0 : thisReasonReports;
  const total = scenarioThisReason + otherReports;

  const outcome = useMemo(
    () =>
      resolveScenario(
        previewRules,
        candidate.reason,
        candidate.reportable_type,
        scenarioThisReason,
        otherReports,
        reasonLabels,
      ),
    [previewRules, candidate.reason, candidate.reportable_type, scenarioThisReason, otherReports, reasonLabels],
  );

  // Where THIS rule stands in the dialled scenario: not yet triggered, in effect, or
  // triggered-but-overridden by a stronger rule.
  const candidateCount = isAnyReasonRule ? total : scenarioThisReason;
  const candidateFires = candidate.enabled && candidateCount >= candidate.threshold;
  const candidateOverridden =
    candidateFires && outcome && ACTION_SEVERITY[outcome.action] > ACTION_SEVERITY[candidate.action];

  function candidateStatusNode() {
    if (!candidate.enabled) {
      return <span className="text-muted">This rule is disabled, so it never contributes.</span>;
    }
    if (!candidateFires) {
      return (
        <span className="text-muted">
          This rule hasn&rsquo;t triggered yet — it needs {candidate.threshold} report(s){" "}
          {isAnyReasonRule ? "in total" : `of ${reasonLabel(candidate.reason, reasonLabels)}`}.
        </span>
      );
    }
    if (candidateOverridden) {
      return (
        <span className="text-warning-emphasis">
          This rule has triggered, but a stronger rule wins here — so it doesn&rsquo;t change the outcome.
        </span>
      );
    }
    return <span className="text-success-emphasis">This rule sets the outcome here.</span>;
  }

  // Dangerous-edit confirmations. isAggressiveHide catches a broad/low-threshold
  // hide; weakensSevereRule catches disabling/weakening a severe reason's safety net.
  function confirmDangerousEdit() {
    if (isAggressiveHide(candidate, severeReasons)) {
      const scope = candidate.reason ? reasonLabel(candidate.reason, reasonLabels) : "any single reason";
      // eslint-disable-next-line no-alert
      return window.confirm(
        `This rule hides content after only ${candidate.threshold} report(s) of ${scope}. ` +
          "Content will be hidden quickly and broadly. Save anyway?",
      );
    }
    if (isEdit && weakensSevereRule(rule, candidate, severeReasons)) {
      // eslint-disable-next-line no-alert
      return window.confirm(
        `You are weakening or disabling the safety rule for ${reasonLabel(rule.reason, reasonLabels)} ` +
          "(a severe category). This reduces protection against that content. Save anyway?",
      );
    }
    return true;
  }

  async function handleSave() {
    if (!confirmDangerousEdit()) return;

    setSaving(true);
    setError(null);
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const headers = { ...authenticatedHeadersHash(), "Content-Type": "application/json", "X-CSRF-Token": csrfToken };
      // On edit only the mutable fields go up (reason/type are fixed).
      const payload = isEdit
        ? { moderation_rule: { action, threshold: Number(threshold), enabled } }
        : {
            moderation_rule: {
              reason: reason || null,
              reportable_type: reportableType || null,
              action,
              threshold: Number(threshold),
              enabled,
            },
          };
      const url = isEdit ? `/admin/api/moderation_rules/${rule.id}` : "/admin/api/moderation_rules";
      const response = await fetch(url, { method: isEdit ? "PATCH" : "POST", headers, body: JSON.stringify(payload) });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setError((data.errors || []).join(", ") || "Failed to save the rule");
        return;
      }
      onSaved();
    } catch {
      setError("Network error - please try again");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal show onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? "Edit rule" : "New rule"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <label htmlFor="rule-reason" className="form-label">
            Reason
          </label>
          <select
            id="rule-reason"
            className="form-select"
            value={reason}
            disabled={isEdit}
            onChange={(e) => setReason(e.target.value)}
          >
            <option value="">{WILDCARD_REASON_LABEL}</option>
            {reasonKeys.map((key) => (
              <option key={key} value={key}>
                {reasonLabel(key, reasonLabels)}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label htmlFor="rule-type" className="form-label">
            Content type
          </label>
          <select
            id="rule-type"
            className="form-select"
            value={reportableType}
            disabled={isEdit}
            onChange={(e) => setReportableType(e.target.value)}
          >
            <option value="">{WILDCARD_TYPE_LABEL}</option>
            {typeKeys.map((key) => (
              <option key={key} value={key}>
                {typeLabel(key, typeLabels)}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label htmlFor="rule-action" className="form-label">
            Action
          </label>
          <select id="rule-action" className="form-select" value={action} onChange={(e) => setAction(e.target.value)}>
            {actionOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label htmlFor="rule-threshold" className="form-label">
            Threshold (distinct reporters)
          </label>
          <input
            id="rule-threshold"
            type="number"
            min="1"
            className="form-control"
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
          />
        </div>

        <div className="form-check mb-3">
          <input
            id="rule-enabled"
            type="checkbox"
            className="form-check-input"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="rule-enabled">
            Enabled
          </label>
        </div>

        <div className="bg-light p-3 rounded">
          <h6 className="text-muted">
            Effect simulator — {reasonLabel(candidate.reason, reasonLabels)} on{" "}
            {typeLabel(candidate.reportable_type, typeLabels)}
          </h6>
          <p className="small text-muted mb-2">
            Dial the report counts to see what the engine does. Any-reason rules count <em>all</em> reports on the item;
            a reason-specific rule counts only its own — so which rule wins depends on the mix.
          </p>

          <div className="row g-2 align-items-end">
            {!isAnyReasonRule && (
              <div className="col-auto">
                <label htmlFor="sim-this-reason" className="form-label small mb-0">
                  Reports of {reasonLabel(candidate.reason, reasonLabels)}
                </label>
                <input
                  id="sim-this-reason"
                  type="number"
                  min="0"
                  className="form-control form-control-sm"
                  style={{ width: "8rem" }}
                  value={thisReasonReports}
                  onChange={(e) => setThisReasonReports(Math.max(0, Number(e.target.value)))}
                />
              </div>
            )}
            <div className="col-auto">
              <label htmlFor="sim-other" className="form-label small mb-0">
                {isAnyReasonRule ? "Total reports (any reason)" : "Reports of other reasons"}
              </label>
              <input
                id="sim-other"
                type="number"
                min="0"
                className="form-control form-control-sm"
                style={{ width: "8rem" }}
                value={otherReports}
                onChange={(e) => setOtherReports(Math.max(0, Number(e.target.value)))}
              />
            </div>
          </div>

          <div className="small mt-2">
            <div>
              <span className="text-muted">Outcome at {total} report(s): </span>
              {outcome ? (
                <span>
                  <strong>{actionLabel(outcome.action, actionOptions)}</strong>{" "}
                  <span className="text-muted">
                    (the rule at {outcome.threshold} report(s) {outcome.scopeLabel})
                  </span>
                </span>
              ) : (
                <span className="fst-italic">no automatic action</span>
              )}
            </div>
            <div className="mt-1">{candidateStatusNode()}</div>
            <div className="text-muted mt-2" style={{ fontSize: "0.8em" }}>
              &ldquo;Other reports&rdquo; count only toward any-reason rules here — a stronger rule for one of those
              reasons could hide sooner, so this scenario weighs this reason against the any-reason net rather than
              every reason at once.
            </div>
          </div>
        </div>

        {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

const RULE_SHAPE = PropTypes.shape({
  id: PropTypes.number,
  reason: PropTypes.string,
  reportable_type: PropTypes.string,
  action: PropTypes.string,
  threshold: PropTypes.number,
  enabled: PropTypes.bool,
});

ModerationRuleModal.propTypes = {
  rule: RULE_SHAPE,
  allRules: PropTypes.arrayOf(RULE_SHAPE).isRequired,
  actionOptions: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })).isRequired,
  reasonKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  reasonLabels: PropTypes.objectOf(PropTypes.string).isRequired,
  typeKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  typeLabels: PropTypes.objectOf(PropTypes.string).isRequired,
  severeReasons: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSaved: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ModerationRuleModal;
